import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Invites, consumeReturnTo } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  TERMS_OF_USE, PRIVACY_NOTICE, CLINICAL_USE_SHORT,
} from "@/lib/legalContent";

const LEGAL_TABS = {
  terms:    { title: "Termos de Utilização",    body: TERMS_OF_USE },
  privacy:  { title: "Política de Privacidade", body: PRIVACY_NOTICE },
  clinical: { title: "Uso Clínico",              body: CLINICAL_USE_SHORT },
};

export default function LoginPage() {
  const { loginWithGoogle, user, loading } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const inviteToken = sp.get("invite");
  const [invite, setInvite] = useState(null);
  const [inviteError, setInviteError] = useState("");
  const [legalOpen, setLegalOpen] = useState(null); // terms | privacy | clinical | null
  const currentLegal = legalOpen ? LEGAL_TABS[legalOpen] : null;

  useEffect(() => { if (user) nav(consumeReturnTo(), { replace: true }); }, [user, nav]);

  useEffect(() => {
    if (!inviteToken) return;
    Invites.info(inviteToken)
      .then(setInvite)
      .catch(() => setInviteError("Convite inválido ou expirado."));
  }, [inviteToken]);

  return (
    <div
      className="min-h-screen bg-slate-950 flex flex-col px-6 pt-safe pb-safe"
      style={{ backgroundImage: "radial-gradient(ellipse at top, rgba(56,189,248,0.08), transparent 60%)" }}
    >
      {/* Spacer flex-1 acima e abaixo dá respiração vertical generosa, com auth card opticamente centrado */}
      <div className="flex-1 flex items-center justify-center">
        <div className="max-w-md w-full fade-in-up" data-testid="login-page">
          {/* Brand block — mais respiração entre logo e subtítulos */}
          <div className="text-center">
            <div
              className="font-display text-5xl sm:text-6xl text-slate-50 tracking-[0.22em]"
              style={{ textShadow: "0 0 32px rgba(56,189,248,0.4)" }}
            >
              MIO
            </div>
            <div className="text-[11px] uppercase tracking-[0.28em] text-slate-500 mt-4">
              Mapping Intelligence for Osteopathy
            </div>
          </div>

          {/* Banners de convite — mantidos com mesma testid */}
          {invite && !invite.used && (
            <div data-testid="invite-banner" className="mt-10 rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4 text-sm text-emerald-100">
              <div className="text-[11px] uppercase tracking-[0.2em] text-emerald-300 font-semibold">Convite</div>
              <div className="mt-1">
                Foste convidado(a) {invite.invited_by_name ? <>por <span className="text-slate-100">{invite.invited_by_name}</span></> : null}.
                Entra com a conta Google associada a <span className="text-emerald-200 font-medium">{invite.email}</span>.
              </div>
            </div>
          )}
          {invite?.used && (
            <div className="mt-10 rounded-xl border border-slate-800 bg-slate-900/50 p-4 text-sm text-slate-300" data-testid="invite-used-banner">
              Este convite já foi aceite. Faz login normalmente.
            </div>
          )}
          {inviteError && (
            <div className="mt-10 rounded-xl border border-rose-500/30 bg-rose-500/5 p-4 text-sm text-rose-200" data-testid="invite-error-banner">
              {inviteError}
            </div>
          )}

          {/* Auth card — minimal, mais ar */}
          <div className="mt-10 rounded-2xl border border-slate-800/80 bg-slate-900/40 backdrop-blur-sm p-7 sm:p-9">
            <div className="text-[10px] uppercase tracking-[0.28em] text-sky-400/90 font-semibold">
              Entrar
            </div>
            <h1 className="font-display text-xl sm:text-2xl text-slate-50 mt-3">
              Acesso profissional
            </h1>
            <p className="text-sm text-slate-400 mt-2 leading-relaxed">
              Entra com a tua conta Google para aceder ao painel clínico.
            </p>

            <Button
              data-testid="google-login-btn"
              onClick={loginWithGoogle}
              disabled={loading}
              className="w-full mt-7 bg-sky-500 hover:bg-sky-400 text-slate-950 rounded-full px-6 h-11 font-semibold glow-sky"
            >
              <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
                <path d="M21.35 11.1h-9.17v2.92h5.25c-.23 1.41-1.64 4.14-5.25 4.14-3.16 0-5.73-2.61-5.73-5.83s2.57-5.83 5.73-5.83c1.8 0 3 .77 3.69 1.43l2.52-2.43C16.74 3.82 14.59 2.9 12.18 2.9c-5.04 0-9.13 4.09-9.13 9.13s4.09 9.13 9.13 9.13c5.27 0 8.77-3.7 8.77-8.9 0-.6-.07-1.06-.15-1.16z" />
              </svg>
              Continuar com Google
            </Button>

            {/* Consentimento — compacto e discreto */}
            <p className="text-[10px] text-slate-600 mt-5 text-center leading-relaxed">
              Ao entrar, aceitas os{" "}
              <button
                data-testid="login-terms-link"
                onClick={() => setLegalOpen("terms")}
                className="underline-offset-2 hover:underline hover:text-slate-400 transition"
              >
                Termos de Utilização
              </button>{" "}
              e a{" "}
              <button
                data-testid="login-privacy-link"
                onClick={() => setLegalOpen("privacy")}
                className="underline-offset-2 hover:underline hover:text-slate-400 transition"
              >
                Política de Privacidade
              </button>
              .
            </p>
          </div>
        </div>
      </div>

      {/* Footer minimal · 3 links discretos · sem card legal */}
      <footer
        data-testid="login-legal-footer"
        className="pt-8 pb-2 flex items-center justify-center gap-4 text-[10px] text-slate-600/80"
      >
        <button
          data-testid="footer-terms-btn"
          onClick={() => setLegalOpen("terms")}
          className="hover:text-slate-400 transition tracking-wide"
        >
          Termos
        </button>
        <span className="text-slate-700/60">·</span>
        <button
          data-testid="footer-privacy-btn"
          onClick={() => setLegalOpen("privacy")}
          className="hover:text-slate-400 transition tracking-wide"
        >
          Privacidade
        </button>
        <span className="text-slate-700/60">·</span>
        <button
          data-testid="footer-clinical-btn"
          onClick={() => setLegalOpen("clinical")}
          className="hover:text-slate-400 transition tracking-wide"
        >
          Uso clínico
        </button>
      </footer>

      <Dialog open={!!legalOpen} onOpenChange={(v) => !v && setLegalOpen(null)}>
        <DialogContent className="bg-slate-950 border-slate-800 text-slate-100 max-w-2xl" data-testid="legal-modal">
          {currentLegal && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-slate-50">{currentLegal.title}</DialogTitle>
                <DialogDescription className="text-slate-500">
                  Versão sintética · texto completo em Definições → Termos e Condições.
                </DialogDescription>
              </DialogHeader>
              <div className="text-sm text-slate-300 leading-relaxed whitespace-pre-line max-h-[60vh] overflow-y-auto pr-2">
                {currentLegal.body}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
